import { useAppStore } from "@/store/appStore";
import { useNavigate } from "@tanstack/react-router";
import { Icon, tokens } from "@vintecc/components";
import { useMemo } from "react";
import { useFlag } from "@openfeature/react-sdk";
import { FEATURE_FLAGS } from "@/helpers/FEATURE_FLAGS";
import { ENV } from "@/env";
import { usePlatformAdminMutations } from "@/services/platformAdmin";
import { AppTileProps } from "@/components/AppTile/types";
import { useCommonTranslation } from "@/services/culture/common.translations";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faBell } from "@vintecc/pro-light-svg-icons/faBell";
import { faBoxOpen } from "@vintecc/pro-light-svg-icons/faBoxOpen";
import { faBuildingUser } from "@vintecc/pro-light-svg-icons/faBuildingUser";
import { faChartScatter } from "@vintecc/pro-light-svg-icons/faChartScatter";
import { faD } from "@vintecc/pro-light-svg-icons/faD";
import { faDiagramSubtask } from "@vintecc/pro-light-svg-icons/faDiagramSubtask";
import { faFileChartLine } from "@vintecc/pro-light-svg-icons/faFileChartLine";
import { faFolderTree } from "@vintecc/pro-light-svg-icons/faFolderTree";
import { faInfoCircle } from "@vintecc/pro-light-svg-icons/faInfoCircle";
import { faPieChart } from "@vintecc/pro-light-svg-icons/faPieChart";
import { faServer } from "@vintecc/pro-light-svg-icons/faServer";
import { faShapes } from "@vintecc/pro-light-svg-icons/faShapes";
import { faSignalStream } from "@vintecc/pro-light-svg-icons/faSignalStream";
import { faSiren } from "@vintecc/pro-light-svg-icons/faSiren";
import { faSitemap } from "@vintecc/pro-light-svg-icons/faSitemap";
import { faStore } from "@vintecc/pro-light-svg-icons/faStore";
import { faTowerBroadcast } from "@vintecc/pro-light-svg-icons/faTowerBroadcast";

const DEFAULT_SUPPORT_URL = "https://vintecc.github.io/Capture.Docs.External/";

// Centralized native app configurations
export const NATIVE_APP_CONFIGS: Record<string, { icon: IconProp; color: string }> = {
  "Identity & access": { icon: faBuildingUser, color: tokens.color.blue[300] },
  Organiser: { icon: faSitemap, color: tokens.color.red[400] },
  Datalogging: { icon: faSignalStream, color: tokens.color.orange[400] },
  Dashboards: { icon: faPieChart, color: tokens.color.green[600] },
  Alerting: { icon: faSiren, color: tokens.color.pink[400] },
  Reporting: { icon: faFileChartLine, color: "#9669ea" },
  Source: { icon: faTowerBroadcast, color: "#cc893b" },
  Storage: { icon: faServer, color: "#cc893b" },
  UNS: { icon: faFolderTree, color: tokens.color.green[600] },
  ResourcesV1: { icon: faServer, color: "#3a8fcc" },
  Resources: { icon: faShapes, color: "#00AEEF" },
  Support: { icon: faInfoCircle, color: "#5887b5" },
  Analytics: { icon: faChartScatter, color: tokens.color.purple[400] },
  Dual: { icon: faD, color: tokens.color.pink[400] },
  Subcompany: { icon: faDiagramSubtask, color: tokens.color.blue[300] },
  Marketplace: { icon: faStore, color: tokens.color.green[600] },
  "Product management": { icon: faBoxOpen, color: tokens.color.orange[400] },
  Notifications: { icon: faBell, color: tokens.color.red[400] },
};

export function useDefaultApps() {
  const { t } = useCommonTranslation();
  const loginAnswer = useAppStore((state) => state.loginAnswer);
  const hasAppAccess = useAppStore((state) => state.hasAppAccess);
  const navigate = useNavigate();
  const { value: showDevices } = useFlag(FEATURE_FLAGS.Devices_Feature_Module_V1_Enabled, false);
  const { value: showOrganiser } = useFlag(FEATURE_FLAGS.Organiser_Feature_Module_V1_Enabled, false);
  const { value: showResourcesV1 } = useFlag(FEATURE_FLAGS.Resources_Feature_Module_V1_Enabled, false);
  const { navigate: navigateMutation } = usePlatformAdminMutations();

  /**
   * This is used for tracking purposes when navigating to another app.
   * @param appName This should not be changed once set, as it is used for tracking.
   */
  const handleAppNavigation = (appName: string, navigationFn: () => void) => {
    navigateMutation.mutate({ location: appName, app: true });
    navigationFn();
  };

  const defaultApps = useMemo((): (AppTileProps & {
    visible: boolean;
    showInNavigation: boolean;
    showTitleInTopbar?: boolean;
    searchParams?: { [key: string]: string };
  })[] => {
    return [
      {
        id: "identity-and-access",
        title: t("apps.identityAndAccess"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Identity & access"].icon} />,
        color: NATIVE_APP_CONFIGS["Identity & access"].color,
        onClick: () => handleAppNavigation("Identity & access", () => navigate({ to: "/identity-and-access/home" })),
        visible: hasAppAccess("Identity & access"),
        showInNavigation: true,
        to: "/identity-and-access",
      },
      {
        id: "organiser",
        title: t("apps.organiser"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Organiser"].icon} />,
        color: NATIVE_APP_CONFIGS["Organiser"].color,
        onClick: () => handleAppNavigation("Organiser", () => navigate({ to: "/capture", search: { path: "/organiser/fleet" } })),
        visible: hasAppAccess("Organiser") && showOrganiser,
        showInNavigation: true,
        to: "/capture",
        searchParams: { path: "/organiser/fleet" },
      },
      {
        id: "devices",
        title: t("apps.devices"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Datalogging"].icon} />,
        color: NATIVE_APP_CONFIGS["Datalogging"].color,
        onClick: () => handleAppNavigation("Devices", () => navigate({ to: "/capture", search: { path: "/devices/overview" } })),
        visible: hasAppAccess("Datalogging") && showDevices,
        showInNavigation: true,
        to: "/capture",
        searchParams: { path: "/devices/overview" },
      },
      {
        id: "dashboards",
        title: t("apps.dashboards"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Dashboards"].icon} />,
        color: NATIVE_APP_CONFIGS["Dashboards"].color,
        onClick: () =>
          handleAppNavigation("Dashboards", () =>
            navigate({
              to: "/dashboards",
            }),
          ),
        visible: hasAppAccess("Dashboards"),
        showInNavigation: true,
        to: "/dashboards",
        showTitleInTopbar: true,
      },
      {
        id: "sources",
        title: t("apps.sources"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Source"].icon} />,
        color: NATIVE_APP_CONFIGS["Source"].color,
        onClick: () => handleAppNavigation("Source", () => navigate({ to: "/sources/home" })),
        visible: hasAppAccess("Datalogging") && hasAppAccess("Source"),
        showInNavigation: true,
        to: "/sources",
      },
      {
        id: "storage-hub",
        title: t("apps.storageHub"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Storage"].icon} />,
        color: NATIVE_APP_CONFIGS["Storage"].color,
        onClick: () => handleAppNavigation("Storage hub", () => navigate({ to: "/storage-hub" })),
        visible: hasAppAccess("Storage"),
        showInNavigation: true,
        to: "/storage-hub",
      },
      {
        id: "uns",
        title: t("apps.uns"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["UNS"].icon} />,
        color: NATIVE_APP_CONFIGS["UNS"].color,
        onClick: () => handleAppNavigation("Datalogging", () => navigate({ to: "/uns/home" })),
        visible: hasAppAccess("Datalogging") && hasAppAccess("Source"),
        showInNavigation: true,
        to: "/uns",
      },
      {
        id: "resources-v1",
        title: t("apps.resources"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["ResourcesV1"].icon} />,
        color: NATIVE_APP_CONFIGS["ResourcesV1"].color,
        onClick: () => handleAppNavigation("Resources", () => navigate({ to: "/capture", search: { path: "/resources/releases" } })),
        visible: hasAppAccess("Resources") && showResourcesV1,
        showInNavigation: true,
        to: "/capture",
        searchParams: { path: "/resources/releases" },
      },
      {
        id: "resources",
        title: t("apps.resources"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Resources"].icon} />,
        color: NATIVE_APP_CONFIGS["Resources"].color,
        onClick: () => handleAppNavigation("Resources", () => navigate({ to: "/resources" })),
        visible: hasAppAccess("Resources"),
        showInNavigation: true,
        to: "/resources",
      },
      {
        id: "support",
        title: t("apps.support"),
        icon: <Icon icon={NATIVE_APP_CONFIGS["Support"].icon} />,
        color: NATIVE_APP_CONFIGS["Support"].color,
        to: ENV.SUPPORT_URL || DEFAULT_SUPPORT_URL,
        isExternalLink: true,
        visible: true,
        showInNavigation: false,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAppAccess, loginAnswer, navigate, t]);

  return defaultApps;
}
